import { cx } from '@linaria/core';
import { useRef, useId, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { ElementToolbar } from '@pm2/element-toolbar';
import { useSpeechSynthesisConfig } from '@provider/speech-synthesis';
import { breakpoints, units } from '@ui/css';
import { SpeechButton, componentFrameworkCloseButton } from '@ui/kit';
import { CrossIcon } from '@ui/kit/icons';
import { findNearestScroller } from '@utils/common';
import { ComponentFrameworkTitle, ComponentFrameworkSaveIndicator } from '@utils/component-framework';
import { useMatchBreakpoint, useFullscreenState, useChangeEffect, useElementSize, SizeTransition, ShowFor } from '@utils/react';
import { selectors } from './_component.selectors.js';
import { elementToolbar, header, saveIndicatorPlaceholder, actionsWrapper, actions, elementToolbarPlaceholder, componentWrapper, saveIndicator, saveIndicatorWrapper, contentWrapper, content, pageFadeDurationMs, mobileFullscreenButton, speechButtonWrapper, speechButtonSticky, wrapper, root } from './_component.style.js';
import { headerSize, actionsSize, tokens } from './_component.tokens.js';
import { OpgavekompleksetContextMenu } from './components/molecule.context-menu/context-menu.js';
import { FlowTypeContext } from './components/molecule.flow-type-context/_flow-type-context.js';
import { useOpgavekompleksetFullscreenActions } from './components/organism.task/molecule.task-header/hook.fullscreen.js';
import { ComponentRootRef } from './context.js';
import { useFocusManagement } from './hook.focus-management.js';
import { usePreloadedIntroductions } from './hook.preloaded-introductions.js';
import { useWarnOnFirstMobileInteraction } from './hook.warn-on-mobile-interaction.js';
import { InputPage } from './page.input/_page.input.js';
import { ResultsPage } from './page.results/_page.results.js';
import { ComponentDataProvider } from './state/context.data.js';
import { useCurrentTaskEntryId, useCurrentTaskEntry } from './state/hook.entry.js';
import { useCurrentLocation } from './state/hook.location.js';
import { makeSelectTaskState } from './state/selectors/task.get-state.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function OpgavekompleksetComponentContent(props) {
  const internalRef = useRef(null);
  const rootRef = props.ref ?? internalRef;
  const headerRef = useRef(null);
  const actionsRef = useRef(null);
  const id = useId();
  const overlayContainerId = `overlay-container-${id}`;
  const rootSelector = `[data-opgavekomplekset-id='${props.component.envelope.ID}']`;
  const activeTaskId = useCurrentTaskEntryId();
  const currentPageId = useCurrentLocation();
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const speechConfig = useSpeechSynthesisConfig();
  const {
    fullscreenEnabled
  } = useFullscreenState();
  const {
    handleFullscreenClick,
    handleExitFullscreenClick
  } = useOpgavekompleksetFullscreenActions({
    componentProps: props,
    overlayId: overlayContainerId,
    rootSelector
  });
  const handleMobileWarningCallback = useWarnOnFirstMobileInteraction(props.component);
  const taskState = useCurrentTaskEntry({
    select: makeSelectTaskState(props.component.data)
  }) ?? "input";

  // NB: Scrolling to top will be handled by the embed integration, but in
  // other contexts we should go to the top of the task.
  useChangeEffect(() => {
    if (fullscreenEnabled) {
      rootRef.current?.scrollTo({
        top: 0,
        behavior: "smooth"
      });
      return;
    }
    if (props.context === "standalone") {
      window.scrollTo({
        behavior: "smooth",
        top: 0
      });
      return;
    }
    if (props.context === "lightbox") {
      const scroller = findNearestScroller(rootRef, "vertical");
      scroller?.scrollTo({
        top: 0,
        behavior: "smooth"
      });

      // In case a modal has been open during navigation (e.g. retry), then the
      // scroller cannot be found as focus-on has set overflow to hidden.
      // In this rare case we wait a little bit before scrolling to the top.
      if (!scroller) {
        setTimeout(() => {
          const scroller = findNearestScroller(rootRef, "vertical");
          scroller?.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        }, 200);
      }
    }
  }, [activeTaskId, currentPageId, taskState]);
  useEffect(() => {
    if (fullscreenEnabled) {
      props.onMountedInFullscreen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreenEnabled]);
  useFocusManagement(rootRef, {
    isExpandedHarmonica: !!props.onCollapse
  });
  usePreloadedIntroductions(props.component.data);
  useElementSize(headerRef, {
    applyTo: rootRef,
    axis: "vertical",
    varName: headerSize.propName
  });
  useElementSize(actionsRef, {
    axis: "vertical",
    applyTo: rootRef,
    varName: actionsSize.propName
  });
  const withSpeechButton = speechConfig.enabled && (props.context !== "embedded" || fullscreenEnabled);
  const onEmbeddedMobileFullscreenClick = () => {
    handleFullscreenClick();
    setTimeout(() => {
      handleMobileWarningCallback();
    }, 150);
  };
  return /*#__PURE__*/jsx(ComponentRootRef.Provider, {
    value: rootRef,
    children: /*#__PURE__*/jsxs("div", {
      ref: rootRef,
      className: cx(props.className, root),
      "data-opgavekomplekset-id": props.component.envelope.ID,
      "data-context": props.context,
      "data-fullscreen": fullscreenEnabled,
      tabIndex: -1,
      children: [/*#__PURE__*/jsxs("section", {
        id: id,
        className: cx(wrapper, withSpeechButton && "withSpeechButton"),
        children: [(currentPageId === "results" && !isMobile || currentPageId === "input" && isMobile) && props.node && !fullscreenEnabled && props.context !== "lightbox" && /*#__PURE__*/jsx("div", {
          className: elementToolbar,
          children: /*#__PURE__*/jsx(ElementToolbar, {
            node: props.node
          })
        }), /*#__PURE__*/jsxs("header", {
          ref: headerRef,
          className: header,
          style: tokens.__override({
            buttonCount: currentPageId === "results" ? 4 : 1,
            buttonWidth: units.px(currentPageId === "results" ? 50 : 40)
          }),
          children: [/*#__PURE__*/jsx("div", {
            className: cx(saveIndicatorPlaceholder)
          }), /*#__PURE__*/jsx(ComponentFrameworkTitle, {
            title: props.component.envelope.Title,
            onCollapse: props.onCollapse,
            richtextDescription: props.context === "embedded" ? props.node?.meta.richTextDescription : undefined
          }), /*#__PURE__*/jsxs("div", {
            ref: actionsRef,
            className: actionsWrapper,
            children: [currentPageId === "results" && /*#__PURE__*/jsx(OpgavekompleksetContextMenu, {
              className: actions,
              component: props.component,
              onShareWithTeacher: props.onShareWithTeacher
            }), (currentPageId === "input" && !isMobile || currentPageId === "results" && isMobile) && /*#__PURE__*/jsx("div", {
              className: elementToolbarPlaceholder,
              children: props.node && !fullscreenEnabled && props.context !== "lightbox" && /*#__PURE__*/jsx(ElementToolbar, {
                node: props.node
              })
            })]
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: componentWrapper,
          children: [/*#__PURE__*/jsx("div", {
            className: cx(saveIndicatorWrapper, currentPageId === "results" && "withContextMenu"),
            children: /*#__PURE__*/jsx("span", {
              className: saveIndicator,
              children: /*#__PURE__*/jsx(ComponentFrameworkSaveIndicator, {
                entryKey: props.component.entryKey
              })
            })
          }), /*#__PURE__*/jsxs("div", {
            className: contentWrapper,
            children: [/*#__PURE__*/jsx(TransitionGroup, {
              component: null,
              children: /*#__PURE__*/jsx(SizeTransition, {
                axis: "vertical",
                swappable: true,
                timeout: pageFadeDurationMs,
                classNames: content,
                delayMs: {
                  enter: 10,
                  exit: 10
                },
                children: ref => /*#__PURE__*/jsx("div", {
                  ref: ref,
                  className: content,
                  children: currentPageId === "input" ? /*#__PURE__*/jsx(InputPage, {
                    componentProps: props,
                    rootSelector: rootSelector,
                    overlayId: overlayContainerId
                  }) : /*#__PURE__*/jsx(ComponentDataProvider, {
                    component: props.component,
                    children: /*#__PURE__*/jsx(ResultsPage, {
                      componentProps: props,
                      onResetEntry: props.onResetEntry,
                      onShareWithTeacher: props.onShareWithTeacher,
                      component: props.component,
                      rootSelector: rootSelector,
                      overlayId: overlayContainerId
                    })
                  })
                })
              }, currentPageId)
            }), /*#__PURE__*/jsx(FlowTypeContext, {})]
          }), isMobile && !fullscreenEnabled && props.context === "embedded" && /*#__PURE__*/jsx("button", {
            className: mobileFullscreenButton,
            "aria-label": "\xC5bn komponenten i fuldsk\xE6rm",
            onClick: onEmbeddedMobileFullscreenClick
          })]
        }), withSpeechButton && /*#__PURE__*/jsx("div", {
          className: speechButtonWrapper,
          children: /*#__PURE__*/jsx("div", {
            className: speechButtonSticky,
            children: /*#__PURE__*/jsx(SpeechButton, {
              rootSelector: `${selectors.wrapper()}[id="${id}"]`,
              flagPlacement: "top",
              tooltipPlacement: "left"
            })
          })
        })]
      }), fullscreenEnabled && /*#__PURE__*/jsx(ShowFor, {
        breakpoint: breakpoints.mobile,
        children: /*#__PURE__*/jsx("button", {
          className: cx(componentFrameworkCloseButton),
          "data-theme": "component-framework",
          "aria-label": "Luk fuldsk\xE6rmsvisning",
          onClick: handleExitFullscreenClick,
          children: /*#__PURE__*/jsx(CrossIcon, {
            height: "13"
          })
        })
      }), /*#__PURE__*/jsx("div", {
        id: overlayContainerId
      })]
    })
  });
}
function OpgavekompleksetComponent(props) {
  return /*#__PURE__*/jsx(ComponentDataProvider, {
    component: props.component,
    children: /*#__PURE__*/jsx(OpgavekompleksetComponentContent, {
      ...props,
      ref: props.ref
    })
  });
}

export { OpgavekompleksetComponent };
//# sourceMappingURL=_component.js.map
